import { z } from 'zod';
/**
 * User-preference keys. The DB stores one row per `(user_id, key)` pair
 * (EAV). Adding a new preference is additive: add the enum value here, add a
 * new arm to `userPreferenceSchema`, and run a Drizzle migration to ALTER the
 * `user_preference_key` pg_enum.
 */
export var UserPreferenceKey;
(function (UserPreferenceKey) {
    UserPreferenceKey["Theme"] = "theme";
})(UserPreferenceKey || (UserPreferenceKey = {}));
/**
 * Appearance theme values exposed by the Preferences tab introduced in
 * KIR-2023.
 *
 * - `dark`   — force dark theme regardless of OS preference
 * - `light`  — force light theme regardless of OS preference
 * - `system` — follow the operating system's color scheme (the default)
 */
export var ThemePreference;
(function (ThemePreference) {
    ThemePreference["Dark"] = "dark";
    ThemePreference["Light"] = "light";
    ThemePreference["System"] = "system";
})(ThemePreference || (ThemePreference = {}));
export const userPreferenceKeySchema = z.nativeEnum(UserPreferenceKey);
export const themeValueSchema = z.nativeEnum(ThemePreference);
/**
 * Per-key value schema — discriminated union keyed by `key`. Mirrors the
 * `policySettingsUpdateSchema` pattern in `policies.ts`. Each new preference
 * pairs its `UserPreferenceKey` literal with a value schema; Zod statically
 * enforces that callers cannot mix a key with a value that doesn't match.
 */
export const userPreferenceSchema = z.discriminatedUnion('key', [
    z.object({
        key: z.literal(UserPreferenceKey.Theme),
        value: themeValueSchema,
    }),
]);
/**
 * Default value for every known preference key. The GET handler merges this
 * registry with the rows it found so the response always contains an entry
 * for every known key — clients never have to special-case "no row yet".
 */
export const DEFAULT_USER_PREFERENCES = {
    [UserPreferenceKey.Theme]: {
        key: UserPreferenceKey.Theme,
        value: ThemePreference.System,
    },
};
/**
 * Response shape for `GET /api/account/preferences`.
 *
 * One element per known preference key, even when the user has never written
 * a value (the default is substituted server-side). Clients can rely on the
 * array containing exactly one entry per `UserPreferenceKey`.
 *
 * Known deferral: a `z.object({ [K in UserPreferenceKey]: ... })` record
 * shape would eliminate the `.refine`, the defense-in-depth parse in
 * `getUserPreferences`, and the duplicate-key illegal state in one stroke
 * — but it is a breaking wire change that would have to land alongside
 * frontend updates (the Preferences UI work in KIR-2321 / KIR-2322 already
 * consumes the array form). The tightening is correct; do it once the
 * second `UserPreferenceKey` is on the roadmap and FE work is staged.
 */
export const userPreferencesResponseSchema = z
    .object({
    preferences: z.array(userPreferenceSchema),
})
    .refine(({ preferences }) => {
    const expected = Object.values(UserPreferenceKey).length;
    const keys = new Set(preferences.map(p => p.key));
    return (preferences.length === expected && keys.size === preferences.length);
}, {
    message: 'preferences must contain exactly one entry per UserPreferenceKey',
});
/**
 * Body for `PUT /api/account/preferences`.
 *
 * Single `{ key, value }` — the route upserts exactly one row, so two
 * concurrent PUTs against different keys cannot clobber each other. To
 * update more than one preference, call PUT once per key.
 */
export const updateUserPreferenceBodySchema = userPreferenceSchema;
//# sourceMappingURL=user-preferences.js.map